import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import Parallax from "./Parallax";

const TIERS = [
  {
    name: "Core Setup & Compliance",
    min: 5,
    rate: 139,
    alt: "$149/month for 12-month",
    features: ["Payroll processing", "Statutory compliance", "HRMS setup", "Bank account & balance sheet management", "Co-branding option available", "Medical insurance coordination"],
    popular: false,
  },
  {
    name: "Managed Operations",
    min: 10,
    rate: 189,
    alt: "$199/month for 12-month",
    features: ["Everything in Core Setup", "Vendor management", "OPEX/CAPEX management", "Invoice approvals", "Admin operations"],
    popular: true,
  },
  {
    name: "Fully Managed / Scale",
    min: 20,
    rate: 229,
    alt: "$249/month for 12-month",
    features: ["Everything in Managed Operations", "Optional real estate/office setup", "Hiring coordination", "Asset lifecycle management", "Operations reporting", "Dedicated admin executive"],
    popular: false,
  },
];

export default function Pricing() {
  const [size, setSize] = useState([15]);

  const { rate, tierName, monthly } = useMemo(() => {
    const n = size[0];
    const tier = n >= 20 ? TIERS[2] : n >= 10 ? TIERS[1] : TIERS[0];
    return { rate: tier.rate, tierName: tier.name, monthly: n * tier.rate };
  }, [size]);

  const fmt = (n) => "$" + n.toLocaleString("en-US");

  return (
    <section id="pricing" className="relative -mt-8 rounded-t-[48px] py-20 sm:py-28 lg:py-32 bg-white" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mb-14"
        >
          <span className="inline-flex items-center rounded-full border border-[#5B4199]/40 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#5B4199] mb-6">
            EOR pricing
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-7xl font-extrabold tracking-tight text-[#5B4199] leading-[1.04]">
            Commercial clarity, with the scope in view.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#475569] leading-relaxed">
            Transparent pricing for Employer of Record services. No hidden fees. Rates shown are for
            24-month contracts.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          {TIERS.map((t, i) => (
            <Parallax key={t.name} distance={i === 1 ? -26 : 26} className="h-full">
              <motion.div
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className={`card-jeton relative h-full rounded-[28px] p-7 sm:p-8 hover:rotate-0 ${
                  t.popular
                    ? "bg-[#5B4199] shadow-[0_32px_70px_-20px_rgba(91,65,153,0.55)] lg:-translate-y-4"
                    : "bg-[#F6F3FE] shadow-[0_24px_56px_-18px_rgba(76,29,149,0.18)]"
                } ${i === 0 ? "lg:-rotate-1" : i === 2 ? "lg:rotate-1" : ""}`}
                data-testid={`pricing-tier-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {t.popular && (
                  <span className="absolute -top-3.5 left-7 bg-[#0B132B] text-white text-[10px] font-mono-tech uppercase tracking-[0.18em] rounded-full px-3.5 py-1.5">
                    Most Popular
                  </span>
                )}
                <div className="flex items-center justify-between">
                  <h3 className={`font-display text-lg font-extrabold ${t.popular ? "text-white" : "text-[#0B132B]"}`}>{t.name}</h3>
                  <span className={`text-[10px] font-mono-tech uppercase tracking-[0.14em] whitespace-nowrap ${t.popular ? "text-white/60" : "text-[#94A3B8]"}`}>
                    Min {t.min} emp
                  </span>
                </div>
                <div className="flex items-baseline gap-1.5 mt-5">
                  <span className={`font-display text-4xl font-extrabold ${t.popular ? "text-white" : "text-[#0B132B]"}`}>${t.rate}</span>
                  <span className={`text-sm ${t.popular ? "text-white/70" : "text-[#475569]"}`}>/emp/mo</span>
                </div>
                <p className={`text-xs mt-1.5 ${t.popular ? "text-white/60" : "text-[#94A3B8]"}`}>{t.alt}</p>
                <ul className={`mt-6 space-y-2.5 border-t pt-6 ${t.popular ? "border-white/15" : "border-[#0B132B]/[0.07]"}`}>
                  {t.features.map((f) => (
                    <li key={f} className={`flex items-start gap-2.5 text-sm ${t.popular ? "text-white/90" : "text-[#475569]"}`}>
                      <span className={`flex h-5 w-5 mt-0.5 shrink-0 items-center justify-center rounded-full ${t.popular ? "bg-white/20" : "bg-white shadow-sm"}`}>
                        <Check className={`h-3 w-3 ${t.popular ? "text-white" : "text-[#5B4199]"}`} />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  data-testid={`pricing-cta-${i}`}
                  className={`mt-7 flex items-center justify-center gap-1.5 rounded-full py-3.5 px-5 text-center text-sm font-semibold transition-colors duration-300 ${
                    t.popular
                      ? "bg-white text-[#0B132B] hover:bg-[#0B132B] hover:text-white"
                      : "bg-white border border-[#0B132B]/10 hover:border-[#5B4199] text-[#0B132B] hover:text-[#5B4199] shadow-sm"
                  }`}
                >
                  Request a scoped proposal
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </motion.div>
            </Parallax>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-sm text-[#475569] max-w-3xl mb-14"
        >
          Pricing is indicative and subject to scope confirmation - it is not a final quote. For ODC
          &amp; CoE pricing, please{" "}
          <a href="#contact" className="font-semibold text-[#5B4199] hover:text-[#0B132B] underline underline-offset-4 transition-colors">
            contact us
          </a>{" "}
          for a customised quote based on your requirements, team size, and infrastructure needs.
        </motion.p>

        <Parallax distance={36}>
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-[32px] bg-[#0B132B] p-8 sm:p-12 shadow-[0_40px_80px_-30px_rgba(11,19,43,0.5)]"
            data-testid="cost-estimator"
          >
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <p className="text-[11px] font-mono-tech uppercase tracking-[0.2em] text-[#A78BFA] mb-3">
                  Indicative estimator
                </p>
                <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-white mb-2">Size your India team.</h3>
                <p className="text-sm text-slate-300/80 mb-8">
                  Drag to see the indicative monthly EOR cost at each tier.
                </p>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-slate-300">Team size</span>
                  <span className="font-mono-tech text-lg text-white" data-testid="estimator-team-size">
                    {size[0]} {size[0] === 1 ? "person" : "people"}
                  </span>
                </div>
                <Slider value={size} onValueChange={setSize} min={1} max={100} step={1} aria-label="Team size" data-testid="estimator-slider" />
                <p className="mt-6 text-xs text-slate-400 leading-relaxed">
                  Indicative only, at 24-month contract rates. Tier applied: {tierName} (${rate}
                  /emp/mo). Final pricing is confirmed in a scoped proposal.
                </p>
              </div>
              <div className="rounded-[28px] bg-[#5B4199] p-8 text-center lg:text-left shadow-[0_24px_60px_-20px_rgba(91,65,153,0.7)]">
                <p className="text-[10px] font-mono-tech uppercase tracking-[0.18em] text-violet-200 mb-2">
                  Indicative monthly cost
                </p>
                <p className="font-display text-4xl sm:text-5xl font-extrabold text-white" data-testid="estimator-expante-cost">
                  {fmt(monthly)}
                </p>
                <p className="text-xs text-violet-200 mt-2">
                  {size[0]} × ${rate}/emp/mo · {tierName}
                </p>
              </div>
            </div>
          </motion.div>
        </Parallax>
      </div>
    </section>
  );
}
